import type { Shell } from '#packages/apilib';
import { EditorAPIKey } from '#packages/apis';
import { EditorPlatformHostIntegrationAPI } from '@wix/editor-platform-host-integration-apis';
import type {
  PluginInfo,
  PluginsHostApiContext,
  WidgetPointer,
  WidgetSlot,
} from '@wix/editor-platform-host-integration-apis';
import type { CompRef } from 'types/documentServices';
import { type Origin } from '../api/document/tpa/widgetPlugins';
import {
  getPluginHostAPIContext,
  getPluginsEditorType,
  isInViewport,
} from './widgetPluginsUtils';

export default (shell: Shell) => {
  const editorAPI = shell.getAPI(EditorAPIKey);

  function getWidgetSlot(slotCompRef: CompRef): WidgetSlot {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return platformAPI.slots.getWidgetSlot(slotCompRef);
  }

  function hasWidgetSlot(widgetCompRef: CompRef): boolean {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return platformAPI.slots.hasWidgetSlot(widgetCompRef);
  }

  function getWidgetSlots(widgetCompRef: CompRef): WidgetSlot[] {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return platformAPI.slots.getWidgetSlots(widgetCompRef);
  }

  async function installWidgetPlugin(
    slotComponentRef: CompRef,
    widgetPointer: WidgetPointer,
    origin: Origin,
  ): Promise<CompRef> {
    const context = getPluginHostAPIContext(origin);
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return await platformAPI.slots.plugins.install(
      slotComponentRef,
      widgetPointer,
      context,
    );
  }

  async function uninstallWidgetPlugin(
    slotComponentRef: CompRef,
    context: PluginsHostApiContext = { origin: '' },
  ): Promise<void> {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return await platformAPI.slots.plugins.uninstall(slotComponentRef, {
      origin: context?.origin,
      editorType: getPluginsEditorType(),
    });
  }

  async function moveWidgetToAnotherSlot(
    fromSlotComponentRef: CompRef,
    toSlotComponentRef: CompRef,
  ) {
    const context = getPluginHostAPIContext({
      origin: 'contextMenu',
      action: 'movePlugin',
    });
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    await platformAPI.slots.moveToSlot(
      fromSlotComponentRef,
      toSlotComponentRef,
      context,
    );
  }

  function getAvailableWidgetSlotsToMoveContent(
    slotComponentRef: CompRef,
  ): Array<{ slotRef: CompRef; name: string }> {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return platformAPI.slots.getAvailableWidgetSlotsToMoveContent(
      slotComponentRef,
    );
  }

  function getWidgetSlotsToMoveContent(slotComponentRef: CompRef): Array<{
    slotRef: CompRef;
    pluginInfo: PluginInfo;
    name: string;
    isPopulated: boolean;
    isSelected: boolean;
  }> {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return platformAPI.slots.getWidgetSlotsToMoveContent(slotComponentRef);
  }

  function unhighlightSlot(compRef: CompRef) {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return platformAPI.slots.unhighlightSlot(compRef);
  }

  async function showOnPage(compRef: CompRef) {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    await platformAPI.slots.plugins.showOnPage(compRef);
    scrollToSlot(compRef);
  }

  async function highlightSlot(compRef: CompRef) {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    await platformAPI.slots.highlightSlot(compRef);
    scrollToSlot(compRef);
  }

  function selectSlot(compRef: CompRef) {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    const slotSelectionCompRef =
      platformAPI.slots.getCompRefForSlotSelection(compRef);
    editorAPI.selection.selectComponentByCompRef(slotSelectionCompRef);
  }

  function scrollToSlot(compRef: CompRef) {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    const slotPosition = platformAPI.slots.getSlotPosition(compRef);

    if (isInViewport(editorAPI, slotPosition)) {
      return;
    }

    const positionOffset = 30;
    editorAPI.ui.scroll.animateScroll(
      {
        y: slotPosition.y - positionOffset,
      },
      0.8,
    );
  }

  function isSlotContentMovable(slotsPlaceholderCompRef: CompRef): boolean {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return platformAPI.slots.isSlotContentMovable(slotsPlaceholderCompRef);
  }

  function isSlotContentPotentialMovable(
    slotsPlaceholderCompRef: CompRef,
  ): boolean {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return platformAPI.slots.isSlotContentPotentialMovable(
      slotsPlaceholderCompRef,
    );
  }

  function isWidgetPluginComponent(widgetPluginCompRef: CompRef): boolean {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return platformAPI.slots.plugins.isPluginComponent(widgetPluginCompRef);
  }

  function isRefComponentVerticalApp(refComponentPointer: CompRef): boolean {
    const { appDefinitionId } =
      editorAPI.components.data.get(refComponentPointer);
    const appData = editorAPI.platform.getAppDataByAppDefId(appDefinitionId);
    if (!appData) return false;
    return appData.components?.some(({ type }) => type === 'PLATFORM');
  }

  function getSlotPlaceholderRefByContentRef(
    compRef: CompRef,
  ): CompRef | undefined {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return platformAPI.slots.getSlotPlaceholderRefByContentRef(compRef);
  }

  function getWidgetHostRefByWidgetRef(compRef: CompRef): CompRef {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return platformAPI.slots.getWidgetHostRefByWidgetRef(compRef);
  }

  function getWidgetHostRefBySlotRef(compRef: CompRef): CompRef {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return platformAPI.slots.getWidgetHostRefBySlotRef(compRef);
  }

  function getSlotInBlocks(slotCompRef: CompRef): WidgetSlot | undefined {
    const platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
    return platformAPI.slots.getSlotInBlocksAppEditor(slotCompRef);
  }

  return {
    hasWidgetSlot,
    getWidgetSlot,
    getSlotInBlocks,
    getWidgetSlots,
    installWidgetPlugin,
    uninstallWidgetPlugin,
    moveWidgetToAnotherSlot,
    scrollToSlot,
    getAvailableWidgetSlotsToMoveContent,
    getWidgetSlotsToMoveContent,
    highlightSlot,
    unhighlightSlot,
    showOnPage,
    selectSlot,
    isSlotContentMovable,
    isSlotContentPotentialMovable,
    isWidgetPluginComponent,
    getSlotPlaceholderRefByContentRef,
    getWidgetHostRefByWidgetRef,
    getWidgetHostRefBySlotRef,
    isRefComponentVerticalApp,
  };
};
